import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeFavorites, saveFavorites } from '@/services/persistenceService';

const ChampionshipContext = createContext();

export const useChampionship = () => {
  const context = useContext(ChampionshipContext);
  if (!context) {
    throw new Error('useChampionship must be used within a ChampionshipProvider');
  }
  return context;
};

export const ChampionshipProvider = ({ children }) => {
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [fingerprintId, setFingerprintId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize favorites from persistent storage (Backend + IndexedDB)
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Initializing favorites from persistent storage...');
        
        const { fingerprintId: fpId, favorites: loadedFavorites } = await initializeFavorites();
        
        setFingerprintId(fpId);
        setFavorites(loadedFavorites);
        setIsInitialized(true);
        
        console.log('✅ Favorites initialized:', {
          fingerprintId: fpId,
          count: loadedFavorites.length,
          favorites: loadedFavorites
        });
      } catch (error) {
        console.error('❌ Error initializing favorites:', error);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadFavorites();
  }, []);

  // Save favorites to persistent storage whenever they change (after initialization)
  useEffect(() => {
    if (isInitialized && fingerprintId && !isLoading) {
      console.log('💾 Saving favorites:', favorites.length);
      saveFavorites(fingerprintId, favorites);
    }
  }, [favorites, isInitialized, fingerprintId, isLoading]);

  const toggleFavorite = (championshipId) => {
    setFavorites((prev) => {
      if (prev.includes(championshipId)) {
        console.log('➖ Removing from favorites:', championshipId);
        return prev.filter((id) => id !== championshipId);
      } else {
        console.log('➕ Adding to favorites:', championshipId);
        return [...prev, championshipId];
      }
    });
  };

  const isFavorite = (championshipId) => {
    return favorites.includes(championshipId);
  };

  const selectChampionship = (championship) => {
    setSelectedChampionship(championship);
  };

  const value = {
    selectedChampionship,
    selectChampionship,
    favorites,
    toggleFavorite,
    isFavorite,
    fingerprintId,
    isLoading,
  };

  return (
    <ChampionshipContext.Provider value={value}>
      {children}
    </ChampionshipContext.Provider>
  );
};
